import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { FC, KeyboardEvent } from 'react';

import { defineMessages, useIntl } from 'react-intl';

import classNames from 'classnames';

import Overlay from 'react-overlays/Overlay';

import GroupsIcon from '@/material-icons/400-24px/group.svg?react';
import LockIcon from '@/material-icons/400-24px/lock.svg?react';
import PublicIcon from '@/material-icons/400-24px/public.svg?react';
import { Icon } from '@/mastodon/components/icon';
import { useAppDispatch, useAppSelector } from '@/mastodon/store';

import { changeComposeCommunity } from '../../../actions/compose';
import { changeComposeVisibility } from '../../../actions/compose_typed';
import { fetchMyCommunities } from '../../../actions/communities';
import type { ApiCommunityJSON } from '../../../api_types/communities';

const messages = defineMessages({
  change_audience: {
    id: 'compose_form.audience.change',
    defaultMessage: 'Change audience',
  },
  public_label: {
    id: 'compose_form.audience.public',
    defaultMessage: 'Public',
  },
  followers_label: {
    id: 'compose_form.audience.followers',
    defaultMessage: 'Followers',
  },
  community_public_suffix: {
    id: 'compose_form.audience.community_public_suffix',
    defaultMessage: '(public)',
  },
  community_private_suffix: {
    id: 'compose_form.audience.community_private_suffix',
    defaultMessage: '(private)',
  },
});

type AudienceValue = 'public' | 'followers' | string; // string = `community:<id>`

const valueForState = (
  privacy: string | null,
  communityId: string | null,
): AudienceValue => {
  if (communityId) return `community:${communityId}`;
  if (privacy === 'private') return 'followers';
  return 'public';
};

interface AudienceItem {
  value: AudienceValue;
  label: string;
  iconKind: 'public' | 'followers' | 'community-public' | 'community-private';
  group: 'core' | 'community';
}

const iconFor = (kind: AudienceItem['iconKind']) => {
  switch (kind) {
    case 'public':
      return PublicIcon;
    case 'followers':
      return LockIcon;
    case 'community-public':
      return GroupsIcon;
    case 'community-private':
      return LockIcon;
  }
};

interface AudienceDropdownProps {
  disabled?: boolean;
}

export const AudienceDropdown: FC<AudienceDropdownProps> = ({
  disabled = false,
}) => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const buttonRef = useRef<HTMLButtonElement>(null);

  const privacy = useAppSelector(
    (state) => state.compose.get('privacy') as string | null,
  );
  const communityId = useAppSelector(
    (state) => state.compose.get('community_id') as string | null,
  );
  const communities = useAppSelector(
    (state) => (state.communities as { mine: ApiCommunityJSON[] }).mine,
  );

  const [open, setOpen] = useState(false);

  useEffect(() => {
    void dispatch(fetchMyCommunities());
  }, [dispatch]);

  const items: AudienceItem[] = useMemo(() => {
    const core: AudienceItem[] = [
      {
        value: 'public',
        label: intl.formatMessage(messages.public_label),
        iconKind: 'public',
        group: 'core',
      },
      {
        value: 'followers',
        label: intl.formatMessage(messages.followers_label),
        iconKind: 'followers',
        group: 'core',
      },
    ];
    const community: AudienceItem[] = communities.map((c) => {
      const isPublic = c.privacy === 'public';
      const suffix = isPublic
        ? intl.formatMessage(messages.community_public_suffix)
        : intl.formatMessage(messages.community_private_suffix);
      return {
        value: `community:${c.id}`,
        label: `${c.name} ${suffix}`,
        iconKind: isPublic ? 'community-public' : 'community-private',
        group: 'community',
      };
    });
    return [...core, ...community];
  }, [communities, intl]);

  const currentValue = valueForState(privacy, communityId);

  const currentItem = useMemo(() => {
    return items.find((it) => it.value === currentValue) ?? items[0];
  }, [items, currentValue]);

  const handleSelect = useCallback(
    (value: AudienceValue) => {
      if (value === 'public') {
        dispatch(changeComposeVisibility('public'));
        dispatch(changeComposeCommunity(null));
      } else if (value === 'followers') {
        dispatch(changeComposeVisibility('private'));
        dispatch(changeComposeCommunity(null));
      } else if (value.startsWith('community:')) {
        const id = value.slice('community:'.length);
        // Communities silo posts server-side; visibility on the wire stays "public".
        dispatch(changeComposeVisibility('public'));
        dispatch(changeComposeCommunity(id));
      }
      setOpen(false);
      buttonRef.current?.focus();
    },
    [dispatch],
  );

  const handleToggle = useCallback(() => {
    setOpen((o) => !o);
  }, []);

  const handleKeyDown = useCallback((e: KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setOpen(true);
    }
  }, []);

  if (!currentItem) return null;

  return (
    <div className='privacy-dropdown'>
      <button
        type='button'
        ref={buttonRef}
        title={intl.formatMessage(messages.change_audience)}
        disabled={disabled}
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
        aria-expanded={open}
        aria-haspopup='listbox'
        className={classNames('dropdown-button', { active: open })}
      >
        <Icon id='audience' icon={iconFor(currentItem.iconKind)} />
        <span className='dropdown-button__label'>{currentItem.label}</span>
      </button>

      <Overlay
        show={open}
        offset={[5, 5]}
        placement='bottom'
        flip
        target={buttonRef}
        popperConfig={{ strategy: 'fixed' }}
      >
        {({ props, placement }) => (
          <div {...props} style={{ ...props.style, zIndex: 1000 }}>
            <div
              className={classNames('privacy-dropdown__dropdown', placement)}
            >
              <AudienceList
                items={items}
                value={currentValue}
                onSelect={handleSelect}
                onClose={() => {
                  setOpen(false);
                }}
              />
            </div>
          </div>
        )}
      </Overlay>
    </div>
  );
};

interface AudienceListProps {
  items: AudienceItem[];
  value: AudienceValue;
  onSelect: (v: AudienceValue) => void;
  onClose: () => void;
}

const AudienceList: FC<AudienceListProps> = ({
  items,
  value,
  onSelect,
  onClose,
}) => {
  const listRef = useRef<HTMLUListElement>(null);

  useEffect(() => {
    const handleDocClick = (e: MouseEvent) => {
      if (
        listRef.current &&
        e.target instanceof Node &&
        !listRef.current.contains(e.target)
      ) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleDocClick, { capture: true });
    return () => {
      document.removeEventListener('mousedown', handleDocClick, {
        capture: true,
      });
    };
  }, [onClose]);

  // Focus the active item on mount for keyboard nav.
  useEffect(() => {
    const el = listRef.current?.querySelector<HTMLLIElement>(
      `[data-value="${CSS.escape(value)}"]`,
    );
    el?.focus({ preventScroll: true });
  }, [value]);

  const handleKey = useCallback(
    (e: KeyboardEvent<HTMLLIElement>) => {
      const li = e.currentTarget;
      const v = li.dataset.value;
      const all = Array.from(
        listRef.current?.querySelectorAll<HTMLLIElement>('li[data-value]') ??
          [],
      );
      const idx = all.findIndex((x) => x === li);

      switch (e.key) {
        case 'Enter':
        case ' ':
          if (v) onSelect(v);
          e.preventDefault();
          break;
        case 'Escape':
          onClose();
          e.preventDefault();
          break;
        case 'ArrowDown':
          all[(idx + 1) % all.length]?.focus();
          e.preventDefault();
          break;
        case 'ArrowUp':
          all[(idx - 1 + all.length) % all.length]?.focus();
          e.preventDefault();
          break;
      }
    },
    [onSelect, onClose],
  );

  const core = items.filter((i) => i.group === 'core');
  const community = items.filter((i) => i.group === 'community');

  return (
    <ul ref={listRef} role='listbox' className='audience-dropdown__list'>
      {core.map((item) => (
        <AudienceOption
          key={item.value}
          item={item}
          active={item.value === value}
          onSelect={onSelect}
          onKeyDown={handleKey}
        />
      ))}
      {community.length > 0 && (
        <li
          role='separator'
          aria-hidden='true'
          className='audience-dropdown__separator'
        />
      )}
      {community.map((item) => (
        <AudienceOption
          key={item.value}
          item={item}
          active={item.value === value}
          onSelect={onSelect}
          onKeyDown={handleKey}
        />
      ))}
    </ul>
  );
};

const AudienceOption: FC<{
  item: AudienceItem;
  active: boolean;
  onSelect: (v: AudienceValue) => void;
  onKeyDown: (e: KeyboardEvent<HTMLLIElement>) => void;
}> = ({ item, active, onSelect, onKeyDown }) => (
  <li
    role='option'
    aria-selected={active}
    tabIndex={0}
    data-value={item.value}
    onKeyDown={onKeyDown}
    onClick={() => {
      onSelect(item.value);
    }}
    className={classNames('privacy-dropdown__option', { active })}
  >
    <div className='privacy-dropdown__option__icon'>
      <Icon id='audience-item' icon={iconFor(item.iconKind)} />
    </div>
    <div className='privacy-dropdown__option__content'>
      <strong>{item.label}</strong>
    </div>
  </li>
);
